import { Button } from '@components'
import { useActiveOrder } from '@haus-storefront-react/hooks'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useCreateStripePaymentIntent } from '../../../../../vendure-plugin-configs/vendure-stripe-plugin'
import { StripeIframeHandler } from './StripeIframeHandler'
import styles from './StripePayment.module.scss'

interface StripePaymentProps {
  onCancel: () => void
  isOpen?: boolean
}

const StripePayment = ({ onCancel, isOpen = true }: StripePaymentProps) => {
  const { t } = useTranslation()
  const { data: activeOrder } = useActiveOrder()
  const { createPaymentIntent, clientSecret, error, isLoading, reset } =
    useCreateStripePaymentIntent()
  const [hasCreatedIntent, setHasCreatedIntent] = useState(false)

  const publishableKey = window.hausStorefront?.stripePublishableKey

  useEffect(() => {
    if (!isOpen) {
      reset()
      setHasCreatedIntent(false)
    }
  }, [isOpen, reset])

  useEffect(() => {
    if (isOpen && activeOrder?.code && !clientSecret && !isLoading && !error && !hasCreatedIntent) {
      setHasCreatedIntent(true)
      void createPaymentIntent().catch(() => undefined)
    }
  }, [
    activeOrder?.code,
    clientSecret,
    createPaymentIntent,
    error,
    hasCreatedIntent,
    isLoading,
    isOpen,
  ])

  if (!publishableKey) {
    return (
      <div className={styles.root}>
        <div className={styles.error} role="alert">
          {t('checkout.paymentMethod.stripeNotConfigured')}
        </div>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t('general.cancel')}
        </Button>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.root}>
        <div className={styles.error} role="alert" aria-live="assertive">
          {t('general.genericErrorMessage')}
        </div>
        <Button type="button" variant="secondary" onClick={onCancel}>
          {t('general.cancel')}
        </Button>
      </div>
    )
  }

  if (isLoading || !clientSecret) {
    return (
      <div className={styles.root}>
        <p aria-busy="true" aria-live="polite">
          {t('checkout.paymentMethod.preparingPayment')}
        </p>
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
          {t('general.cancel')}
        </Button>
      </div>
    )
  }

  if (!activeOrder?.code) {
    return null
  }

  return (
    <StripeIframeHandler
      clientSecret={clientSecret}
      publishableKey={publishableKey}
      orderCode={activeOrder.code}
      onCancel={onCancel}
    />
  )
}

export default StripePayment
